import React from "react";
import { Button, Divider, Popover, Space, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;

const sectionLabelStyle = {
  fontSize: "13px",
  fontWeight: 600,
};

const formatLabel = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== "";
};

const renderTags = (items) => {
  const values = Array.isArray(items) ? items : [items];

  return (
    <Space size={[4, 4]} wrap>
      {values.map((item, index) => (
        <Tag key={`${item}-${index}`} style={{ marginInlineEnd: 0 }}>
          {String(item)}
        </Tag>
      ))}
    </Space>
  );
};

const renderObjectRows = (data) => {
  const entries = Object.entries(data || {}).filter(([, value]) =>
    hasValue(value)
  );

  if (!entries.length) return <Text type="secondary">N/A</Text>;

  return (
    <div style={{ display: "grid", gap: "6px" }}>
      {entries.map(([key, value]) => (
        <div key={key}>
          <Text type="secondary" style={sectionLabelStyle}>
            {formatLabel(key)}
          </Text>
          <div style={{ marginTop: "2px" }}>
            {Array.isArray(value) ? (
              renderTags(value)
            ) : value && typeof value === "object" ? (
              renderObjectRows(value)
            ) : (
              <Text>{String(value)}</Text>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const QueryFindingInfoButton = ({ queryFinding }) => {
  const isDeveloper = localStorage.getItem("role") === "developer";

  if (!isDeveloper || !hasValue(queryFinding)) return null;

  const {
    supervisor_decision,
    customer_intent,
    query_type,
    entity,
    possible_products,
    request_for_agents,
    customer_information,
    reasoning,
  } = queryFinding;

  const content = (
    <div
      style={{
        width: 420,
        maxWidth: "80vw",
        maxHeight: 520,
        overflowY: "auto",
      }}
    >
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Text strong>Query Finding</Text>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px 12px",
          }}
        >
          {hasValue(supervisor_decision) && (
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Supervisor
              </Text>
              <div>
                <Tag color="blue">{supervisor_decision}</Tag>
              </div>
            </div>
          )}
          {hasValue(customer_intent) && (
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Intent
              </Text>
              <div>
                <Text>{customer_intent}</Text>
              </div>
            </div>
          )}
          {hasValue(query_type) && (
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Query Type
              </Text>
              <div>
                <Text>{query_type}</Text>
              </div>
            </div>
          )}
          {hasValue(entity) && (
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Entity
              </Text>
              <div>
                <Text>{entity}</Text>
              </div>
            </div>
          )}
        </div>

        {hasValue(possible_products) && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Possible Products
              </Text>
              <div style={{ marginTop: "4px" }}>{renderTags(possible_products)}</div>
            </div>
          </>
        )}

        {hasValue(customer_information) && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Customer Information
              </Text>
              <div style={{ marginTop: "4px" }}>
                {renderObjectRows(customer_information)}
              </div>
            </div>
          </>
        )}

        {hasValue(request_for_agents) && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Request For Agents
              </Text>
              <Paragraph style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>
                {request_for_agents}
              </Paragraph>
            </div>
          </>
        )}

        {hasValue(reasoning) && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <div>
              <Text type="secondary" style={sectionLabelStyle}>
                Reasoning
              </Text>
              <div style={{ marginTop: "4px" }}>{renderObjectRows(reasoning)}</div>
            </div>
          </>
        )}
      </Space>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="leftTop">
      <Tooltip title="Query finding">
        <Button
          type="text"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={(event) => event.stopPropagation()}
          style={{ color: "#1677ff", flexShrink: 0 }}
        />
      </Tooltip>
    </Popover>
  );
};

export default QueryFindingInfoButton;
